import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { fetchForms, createForm, updateForm } from '../../../../Redux/ActionCreators/onboardingAction';

// ─── Form builder constants (mirrored from FormsPage) ─────────────────────────

const CATEGORIES = [
  'Personal information',
  'Compliance & verification',
  'Payroll & benefits',
  'IT & access',
  'Other',
];

const FIELD_TYPES = [
  { value: 'text',     label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'email',    label: 'Email' },
  { value: 'phone',    label: 'Phone' },
  { value: 'date',     label: 'Date' },
  { value: 'number',   label: 'Number' },
  { value: 'select',   label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file',     label: 'File Upload' },
];

const defaultField = { label: '', type: 'text', required: false, options: '' };
const defaultForm  = { name: '', description: '', category: '', fields: [] };

const normalizeFormForEdit = (form) => {
  if (!form) return defaultForm;
  return {
    ...form,
    category: form.category || '',
    fields: (form.fields || []).map(f => ({
      ...f,
      options: Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''),
    })),
  };
};

const normalizeFormPayload = (d) => ({
  ...d,
  status: 'active',
  fields: (d.fields || []).map(f => {
    const { id: localId, ...rest } = f;
    const field = {
      ...rest,
      options: typeof f.options === 'string'
        ? f.options.split(',').map(o => o.trim()).filter(Boolean)
        : (Array.isArray(f.options) ? f.options : []),
    };
    if (localId && typeof localId === 'string') field.id = localId;
    return field;
  }),
});

// ─── Form Builder Modal ───────────────────────────────────────────────────────

const FormBuilderModal = ({ form, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [data, setData]             = useState(() => normalizeFormForEdit(form));
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);
  const [categoryError, setCategoryError] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const setField = (key, val) => setData(d => ({ ...d, [key]: val }));

  const addField = () =>
    setData(d => ({ ...d, fields: [...d.fields, { ...defaultField, id: Date.now() }] }));

  const updateFieldAt = (idx, key, val) => {
    const next = data.fields.map((f, i) => (i === idx ? { ...f, [key]: val } : f));
    setData(d => ({ ...d, fields: next }));
    if (key === 'label' && val.trim()) {
      setFieldErrors(prev => prev.map((err, i) => (i === idx ? false : err)));
    }
  };

  const removeFieldAt = (idx) => {
    setData(d => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));
    setFieldErrors(prev => prev.filter((_, i) => i !== idx));
  };

  const moveField = (idx, dir) => {
    const next = [...data.fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setData(d => ({ ...d, fields: next }));
    setFieldErrors(prev => {
      const errs = [...prev];
      [errs[idx], errs[target]] = [errs[target], errs[idx]];
      return errs;
    });
  };

  const handleSave = async () => {
    if (!data.name.trim()) { setError('Form name is required.'); return; }

    if (!data.category) {
      setCategoryError(true);
      setError('Category is required.');
      return;
    }

    if (data.fields.length === 0) {
      setError('Please add at least one field before saving.');
      return;
    }

    const labelErrors = data.fields.map(f => !f.label.trim());
    if (labelErrors.some(Boolean)) {
      setFieldErrors(labelErrors);
      setError('All fields must have a label before saving.');
      return;
    }
    setCategoryError(false);
    setFieldErrors([]);
    setError('');
    setSaving(true);
    const payload = normalizeFormPayload(data);
    const res = data.id
      ? await dispatch(updateForm(data.id, payload))
      : await dispatch(createForm(payload));
    setSaving(false);
    if (res && !res.__error) {
      onSaved(res);
    } else {
      const detail = Array.isArray(res?.detail) && res.detail.length ? res.detail.join(', ') : null;
      setError(detail || res?.message || 'Failed to save form.');
    }
  };

  return (
    <div className="upload-modal" style={{ zIndex: 1100 }}>
      <div
        className="modal-content"
      >
        <div className="modal-head">
          <div />
          <h3>{data.id ? 'Edit Form' : 'Create Form'}</h3>
          <img src="/images/close-icon.svg" alt="close" onClick={onClose} style={{ cursor: 'pointer' }} title='Close'/>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ maxHeight: '55vh', overflowY: 'auto', top: '50%', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            <div className="ob-field">
              <label>Form Name <span className="required">*</span></label>
              <input
                type="text"
                placeholder="e.g. Personal Details Form"
                value={data.name}
                onChange={e => setField('name', e.target.value)}
              />
            </div>

            <div className="ob-field">
              <label>Category <span className="required">*</span></label>
              <select
                value={data.category}
                onChange={e => { setField('category', e.target.value); setCategoryError(false); }}
                style={categoryError ? { borderColor: '#ef4444' } : {}}
              >
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="ob-field">
              <label>Description <span className="optional">(optional)</span></label>
              <textarea
                rows={2}
                placeholder="Brief description…"
                value={data.description}
                onChange={e => setField('description', e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom:'12px' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>Fields</span>
              <button className="ob-btn ob-btn--sm ob-btn--primary" onClick={addField}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Field
              </button>
            </div>

            {data.fields.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#8a9199', fontSize: 13, background: '#f4f5f7', borderRadius: 8 }}>
                No fields yet. Click "Add Field" to start building your form.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 300, overflowY: 'auto' }}>
              {data.fields.map((field, idx) => (
                <div key={field.id || idx} style={{ padding: '12px 14px', border: '1px solid #e4e9f0', borderRadius: 8, background: '#fff' }}>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <input
                      style={{
                        flex: 1, padding: '7px 10px', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit',
                        border: `1px solid ${fieldErrors[idx] ? '#ef4444' : '#e4e9f0'}`,
                      }}
                      placeholder="Field label"
                      value={field.label}
                      onChange={e => updateFieldAt(idx, 'label', e.target.value)}
                    />
                    <select
                      style={{ padding: '7px 10px', border: '1px solid #e4e9f0', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                      value={field.type}
                      onChange={e => updateFieldAt(idx, 'type', e.target.value)}
                    >
                      {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  {field.type === 'select' && (
                    <input
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #e4e9f0', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit', marginBottom: 8 }}
                      placeholder="Options (comma separated): Option 1, Option 2"
                      value={field.options}
                      onChange={e => updateFieldAt(idx, 'options', e.target.value)}
                    />
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#525b66', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={e => updateFieldAt(idx, 'required', e.target.checked)}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      Required
                    </label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="ob-btn ob-btn--icon-only ob-btn--sm" onClick={() => moveField(idx, -1)} title="Move up" disabled={idx === 0}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15" /></svg>
                      </button>
                      <button className="ob-btn ob-btn--icon-only ob-btn--sm" onClick={() => moveField(idx, 1)} title="Move down" disabled={idx === data.fields.length - 1}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                      </button>
                      <button className="ob-btn ob-btn--icon-only ob-btn--sm ob-btn--danger" onClick={() => removeFieldAt(idx)} title="Remove">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div ref={errorRef} className="ob-hint ob-hint--warn" style={{marginTop:'20px'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {error}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
            <button className="ob-btn" onClick={onClose}>Cancel</button>
            <button className="upload-btn" onClick={handleSave} disabled={saving} style={{ margin: 0 }}>
              {saving ? 'Saving…' : 'Save Form'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Forms Step ───────────────────────────────────────────────────────────────

const FormsStep = ({ formData, update }) => {
  const dispatch = useDispatch();
  const forms = formData.forms || {};
  const selectedFormIds = forms.selectedForms || [];

  const [availableForms, setAvailableForms] = useState([]);
  const [loading, setLoading]               = useState(false);
  const [showModal, setShowModal]           = useState(false);
  const [modalSelected, setModalSelected]   = useState([]);
  const [showBuilder, setShowBuilder]       = useState(false);
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'success' });

  const loadForms = () => {
    setLoading(true);
    dispatch(fetchForms(1, 100)).then(res => {
      setAvailableForms(res?.data || []);
      setLoading(false);
    });
  };

  useEffect(() => { loadForms(); }, [dispatch]);

  const attachedForms  = availableForms.filter(f => selectedFormIds.some(id => String(id) === String(f.id)));
  const unattachedForms = availableForms.filter(f => !selectedFormIds.some(id => String(id) === String(f.id)));

  const openModal = () => { setModalSelected([]); setShowModal(true); };

  const toggleModalForm = (formId) => {
    setModalSelected(prev =>
      prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId]
    );
  };

  const handleAdd = () => {
    update('forms', { ...forms, selectedForms: [...selectedFormIds, ...modalSelected] });
    setShowModal(false);
    setModalSelected([]);
  };

  const removeForm = (formId) => {
    update('forms', {
      ...forms,
      selectedForms: selectedFormIds.filter(id => String(id) !== String(formId)),
    });
  };

  const handleBuilderSaved = (res) => {
    setSnackbar({ open: true, message: 'Form created successfully.', severity: 'success' });
    setShowBuilder(false);
    setShowModal(true);
    dispatch(fetchForms(1, 100)).then(r => {
      const newForms = r?.data || [];
      setAvailableForms(newForms);
      const newId = res?.data?.id ?? res?.id;
      if (newId && !selectedFormIds.some(id => String(id) === String(newId)) && !modalSelected.includes(newId)) {
        setModalSelected(prev => [...prev, newId]);
      }
    });
  };

  return (
    <div>
      <div style={{ background: '#fff', border: '1px solid #e4e9f0', borderRadius: 12, padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>Forms in this flow</div>
          <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 99 }}>
            {attachedForms.length} attached
          </span>
        </div>

        {loading ? (
          <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attachedForms.map(form => {
              const fieldCount = form.fieldsCount ?? form.fields_count ?? (form.fields?.length ?? 0);
              return (
                <div key={form.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid #e4e9f0', borderRadius: 8, background: '#fff' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#ede9fe', color: '#5b21b6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{form.name}</div>
                    <div style={{ fontSize: 11, color: '#8a9199', marginTop: 2 }}>
                      Custom · {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 99, flexShrink: 0 }}>
                    Custom
                  </span>
                  <button
                    type="button"
                    onClick={() => removeForm(form.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#ef4444', display: 'flex', alignItems: 'center' }}
                    title="Remove form"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  </button>
                </div>
              );
            })}

            {attachedForms.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '24px 16px', border: '1.5px dashed #e4e9f0', borderRadius: 8, color: '#8a9199', fontSize: 13 }}>
                No forms attached yet. Click <strong>+ Add Form</strong> to attach forms to this flow.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, paddingTop: 16, borderTop: '1px solid #e4e9f0' }}>
          <button className="ob-btn ob-btn--primary" type="button" onClick={openModal}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Form
          </button>
        </div>
      </div>

      {/* ── Select / Create forms modal ─────────────────────────────────────── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 520, maxHeight: '78vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Select Forms</div>
                <div style={{ fontSize: 12, color: '#8a9199', marginTop: 3 }}>Choose forms to attach or create a new one</div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 18 }}
              >
                ×
              </button>
            </div>

            {/* Create new form button */}
            <button
              className="ob-btn ob-btn--primary"
              type="button"
              onClick={() => { setShowModal(false); setShowBuilder(true); }}
              style={{ marginBottom: 14, justifyContent: 'center' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create New Form
            </button>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1, height: 1, background: '#e4e9f0' }} />
              <span style={{ fontSize: 11, color: '#9aa3b0', whiteSpace: 'nowrap' }}>or choose from existing</span>
              <div style={{ flex: 1, height: 1, background: '#e4e9f0' }} />
            </div>

            {/* Form list */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {unattachedForms.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', border: '1.5px dashed #e4e9f0', borderRadius: 8, color: '#8a9199', fontSize: 13 }}>
                  {availableForms.length === 0
                    ? 'No forms yet. Create one above to get started.'
                    : 'All available forms are already attached.'}
                </div>
              ) : (
                unattachedForms.map(f => {
                  const fieldCount = f.fieldsCount ?? f.fields_count ?? (f.fields?.length ?? 0);
                  const selected   = modalSelected.includes(f.id);
                  return (
                    <div
                      key={f.id}
                      onClick={() => toggleModalForm(f.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        border: `1px solid ${selected ? '#f75891' : '#e4e9f0'}`,
                        borderRadius: 8, background: selected ? '#fdf2f8' : '#fff',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleModalForm(f.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                      />
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: '#ede9fe', color: '#5b21b6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{f.name}</div>
                        <div style={{ fontSize: 11, color: '#8a9199', marginTop: 2 }}>
                          Custom · {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid #e4e9f0' }}>
              <button className="ob-btn" type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button
                className="ob-btn ob-btn--primary"
                type="button"
                onClick={handleAdd}
                disabled={modalSelected.length === 0}
              >
                Add{modalSelected.length > 0 ? ` (${modalSelected.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Form builder (nested above the selection modal) ─────────────────── */}
      {showBuilder && (
        <FormBuilderModal
          form={null}
          onClose={() => { setShowBuilder(false); setShowModal(true); }}
          onSaved={handleBuilderSaved}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FormsStep;
